import {
  isPresenceStatus,
  parseServerMessage,
  type CallInvitePayload,
  type CallScreenPayload,
  type CallCameraPayload,
  type CallRosterPayload,
  type CallStatePayload,
  type ChatMessage,
  type ChatReactionSummary,
  type ChatRoomId,
  type ChatScope,
  type NotificationEvent,
  type PresenceEntry,
  type PresenceStatus,
} from "@xop/dashboard-shared";

import { refreshAccessToken } from "../api.js";
import { env } from "../env.js";
import { tokenStore } from "../tokenStore.js";

/**
 * Browser WebSocket client for `/api/realtime` — the successor to the SSE
 * notification stream (`notificationStream.ts`). One always-open, auto-
 * reconnecting connection that:
 *
 *   - receives live notification pushes (→ `onEvent`, same payload the SSE
 *     stream delivered, so the popup / snackbar / bell consumers are
 *     unchanged);
 *   - carries the user's EXPLICIT **status** (active / away / dnd) up to the
 *     server (`setStatus`) — persisted in localStorage and re-sent on every
 *     connect so it survives reloads and server restarts;
 *   - reports this client's **idleness** (`setIdle`) as a separate, ephemeral
 *     signal the server aggregates across the user's devices;
 *   - surfaces **presence** (who's online) via `onPresence`.
 *
 * The token rides in the query string (browsers can't set an Authorization
 * header on a WS handshake). On an auth-close (4001) we rotate the access
 * token and reconnect, mirroring the SSE client. Everything external is
 * injectable so the reconnect / status / dispatch logic is unit-testable
 * without a real socket.
 */
export interface RealtimeHandlers {
  onEvent: (ev: NotificationEvent) => void;
  onChat?: (message: ChatMessage) => void;
  /** An existing message was edited or deleted (carries the full updated
   * message; a delete is already blanked into a tombstone). */
  onMessageUpdate?: (message: ChatMessage) => void;
  /** The authenticated user's own id, from the server `hello` frame. Used to
   * tell my own chat echoes from teammates' messages without coupling to the
   * auth context. */
  onSelfId?: (userId: number) => void;
  /** Someone is typing in a conversation (ephemeral). */
  onTyping?: (info: {
    from: number;
    sender_name: string;
    scope: ChatScope;
    room: ChatRoomId | null;
  }) => void;
  /** A DM read receipt — `reader_id` has read up to `last_read_id`. */
  onRead?: (info: { reader_id: number; last_read_id: number }) => void;
  /** A message's emoji reactions changed (carries routing + the new set). */
  onReaction?: (info: {
    message_id: number;
    scope: ChatScope;
    room: ChatRoomId | null;
    sender_id: number;
    recipient_id: number | null;
    reactions: ChatReactionSummary[];
  }) => void;
  onStatus?: (status: PresenceStatus) => void;
  onPresence?: (online: PresenceEntry[]) => void;
  /** An inbound call invite — pushed to the callee the instant a call starts. */
  onCallInvite?: (payload: CallInvitePayload) => void;
  /** A call state transition — pushed to both parties for every state change. */
  onCallState?: (payload: CallStatePayload) => void;
  /** Screen-share signal — the other party started or stopped sharing their
   * screen. Phase 3 Track A. */
  onCallScreen?: (payload: CallScreenPayload) => void;
  /** Camera signal — the other party turned their camera on or off. */
  onCallCamera?: (payload: CallCameraPayload) => void;
  /** Participant roster for a (group) call — pushed on every join/leave/invite. */
  onCallRoster?: (payload: CallRosterPayload) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

/** The slice of the browser `WebSocket` API we use (so tests can fake it). */
export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: (() => void) | null;
}

export interface RealtimeSocketOptions {
  createWebSocket?: (url: string) => WebSocketLike;
  buildUrl?: () => string | null;
  refresh?: () => Promise<boolean>;
  loadStatus?: () => PresenceStatus;
  saveStatus?: (s: PresenceStatus) => void;
  baseBackoffMs?: number;
  maxBackoffMs?: number;
  pingMs?: number;
}

export interface RealtimeSocket {
  /** Persist (localStorage) + push the user's EXPLICIT status choice. Use for
   * profile-menu / tray picks — survives reloads. */
  setStatus(status: PresenceStatus): void;
  /**
   * Report whether THIS client currently sees the user as idle (auto-away).
   * Deliberately NOT a status push: the server aggregates idleness across all
   * of the user's connections, so a tray-resident desktop app going idle can
   * no longer mark them away while a live browser tab is in use. Remembered
   * in-memory and re-announced on reconnect; never persisted, so it can't
   * corrupt the explicit base restored on reload.
   */
  setIdle(idle: boolean): void;
  /** The EXPLICIT status we'll send on (re)connect (never an auto-away). */
  getStatus(): PresenceStatus;
  /** Emit a "typing…" signal for a conversation ("all" or a recipient id).
   * Fire-and-forget — dropped silently if the socket isn't open. */
  sendTyping(to: ChatRoomId | number): void;
  /** Tear down — no further reconnects. */
  close(): void;
}

const STATUS_KEY = "xop.realtime.status";
const WS_OPEN = 1; // WebSocket.OPEN
const AUTH_CLOSE_CODE = 4001; // server's "unauthorized" close

function defaultBuildUrl(): string | null {
  const token = tokenStore.getAccess();
  if (!token) return null;
  // env.apiUrl is "" on web (same-origin) or an absolute https URL in the
  // desktop build. http→ws / https→wss; "" derives from the page origin.
  const base = env.apiUrl
    ? env.apiUrl.replace(/^http/i, "ws")
    : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;
  return `${base}/api/realtime?token=${encodeURIComponent(token)}`;
}

function defaultLoadStatus(): PresenceStatus {
  try {
    const v = localStorage.getItem(STATUS_KEY);
    return isPresenceStatus(v) ? v : "active";
  } catch {
    return "active";
  }
}

function defaultSaveStatus(s: PresenceStatus): void {
  try {
    localStorage.setItem(STATUS_KEY, s);
  } catch {
    /* private mode / quota — non-fatal */
  }
}

export function startRealtimeSocket(
  handlers: RealtimeHandlers,
  opts: RealtimeSocketOptions = {},
): RealtimeSocket {
  const createWebSocket =
    opts.createWebSocket ?? ((url) => new WebSocket(url) as unknown as WebSocketLike);
  const buildUrl = opts.buildUrl ?? defaultBuildUrl;
  const refresh = opts.refresh ?? refreshAccessToken;
  const loadStatus = opts.loadStatus ?? defaultLoadStatus;
  const saveStatus = opts.saveStatus ?? defaultSaveStatus;
  const baseBackoff = opts.baseBackoffMs ?? 1_000;
  const maxBackoff = opts.maxBackoffMs ?? 30_000;
  const pingMs = opts.pingMs ?? 25_000;

  // The EXPLICIT base only — an auto-away never lands here, so a reconnect
  // re-announces what the user actually picked.
  let status: PresenceStatus = loadStatus();
  let idle = false;
  let ws: WebSocketLike | null = null;
  let stopped = false;
  let backoff = baseBackoff;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  function send(obj: unknown): void {
    if (ws && ws.readyState === WS_OPEN) {
      try {
        ws.send(JSON.stringify(obj));
      } catch {
        /* socket closing — reconnect logic will recover */
      }
    }
  }

  function stopPing(): void {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  }

  function scheduleReconnect(): void {
    if (stopped || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, backoff);
    backoff = Math.min(backoff * 2, maxBackoff);
  }

  function connect(): void {
    if (stopped) return;
    const url = buildUrl();
    if (!url) {
      scheduleReconnect(); // no token yet — try again after backoff
      return;
    }
    let socket: WebSocketLike;
    try {
      socket = createWebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }
    ws = socket;

    socket.onopen = () => {
      backoff = baseBackoff;
      send({ t: "status", status }); // restore our chosen status server-side
      send({ t: "activity", idle }); // …and re-announce this client's idleness
      handlers.onOpen?.();
      stopPing();
      pingTimer = setInterval(() => send({ t: "ping" }), pingMs);
    };

    socket.onmessage = (ev) => {
      const raw = typeof ev.data === "string" ? ev.data : String(ev.data);
      const msg = parseServerMessage(raw);
      if (!msg) return;
      switch (msg.t) {
        case "notification":
          handlers.onEvent(msg.event);
          break;
        case "chat_message":
          handlers.onChat?.(msg.message);
          break;
        case "chat_message_update":
          handlers.onMessageUpdate?.(msg.message);
          break;
        case "chat_typing":
          handlers.onTyping?.({
            from: msg.from,
            sender_name: msg.sender_name,
            scope: msg.scope,
            room: msg.room,
          });
          break;
        case "chat_read":
          handlers.onRead?.({ reader_id: msg.reader_id, last_read_id: msg.last_read_id });
          break;
        case "chat_reaction":
          handlers.onReaction?.({
            message_id: msg.message_id,
            scope: msg.scope,
            room: msg.room,
            sender_id: msg.sender_id,
            recipient_id: msg.recipient_id,
            reactions: msg.reactions,
          });
          break;
        case "hello":
          handlers.onSelfId?.(msg.user_id);
          handlers.onStatus?.(msg.status);
          handlers.onPresence?.(msg.online);
          break;
        case "status":
          // The server's EFFECTIVE status (explicit base merged with every
          // device's idleness) — display only. It is deliberately NOT assigned
          // to `status`: that holds our explicit base, and inheriting an
          // aggregate auto-away would re-send "away" as an explicit pick on the
          // next reconnect and pin the user there.
          handlers.onStatus?.(msg.status);
          break;
        case "presence":
          handlers.onPresence?.(msg.online);
          break;
        case "call_invite":
          handlers.onCallInvite?.(msg.call);
          break;
        case "call_state":
          handlers.onCallState?.({
            call_id: msg.call_id,
            state: msg.state,
            ...(msg.reason !== undefined ? { reason: msg.reason } : {}),
            ...(msg.room !== undefined ? { room: msg.room } : {}),
          });
          break;
        case "call_screen":
          handlers.onCallScreen?.({
            call_id: msg.call_id,
            sharing: msg.sharing,
            sharer_session_id: msg.sharer_session_id,
          });
          break;
        case "call_camera":
          handlers.onCallCamera?.({
            call_id: msg.call_id,
            sharing: msg.sharing,
            sharer_session_id: msg.sharer_session_id,
          });
          break;
        case "call_roster":
          handlers.onCallRoster?.({ call_id: msg.call_id, participants: msg.participants });
          break;
        case "pong":
          break;
      }
    };

    socket.onclose = (ev) => {
      stopPing();
      ws = null;
      handlers.onClose?.();
      if (stopped) return;
      if (ev.code === AUTH_CLOSE_CODE) {
        // Token rejected (likely expired) — rotate it, then reconnect.
        void refresh()
          .then((ok) => {
            if (ok) backoff = baseBackoff;
          })
          .catch(() => undefined)
          .finally(() => scheduleReconnect());
      } else {
        scheduleReconnect();
      }
    };

    socket.onerror = () => {
      /* an onclose always follows; recovery happens there */
    };
  }

  connect();

  return {
    setStatus(next) {
      status = next;
      saveStatus(next); // explicit choice — persist as the base for reloads
      send({ t: "status", status: next });
    },
    setIdle(next) {
      if (next === idle) return;
      idle = next; // remembered for reconnect, but deliberately NOT persisted
      send({ t: "activity", idle: next });
    },
    getStatus() {
      return status;
    },
    sendTyping(to) {
      send({ t: "chat_typing", to });
    },
    close() {
      stopped = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      stopPing();
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      ws = null;
    },
  };
}
